/**
 * Flyt — hele artikkelløypa som fem kolonner.
 *
 * Den eneste visningen som viser alt i bevegelse samtidig: hva som er på vei
 * inn, hva som skrives akkurat nå, hva som venter på deg, hva som er planlagt,
 * og hvordan det som ble publisert presterer.
 *
 * Drag-and-drop er begrenset til de overgangene du FAKTISK styrer:
 * «Til godkjenning» → «Planlagt» eller «Publisert», og «Planlagt» → «Publisert».
 * Resten er hendelsesdrevet (AI-en styrer `generating`, publisert kan ikke angres),
 * og markeres derfor ikke som droppsone — gesten skal ikke kunne lyve.
 *
 * HTML5 drag-and-drop virker ikke på touch. Knappene på kortet er den primære
 * veien; draget er en snarvei på desktop.
 */
import React from 'react';
import { Loader2, Eye, Calendar, MoreVertical, Link2 } from 'lucide-react';
import type { Article } from '../../../types';
import { Button } from '../../ui/Button';
import { formatWordCount } from '../../../utils/articleText';
import ViewSparkline from './ViewSparkline';

/** Trinnene i _generateArticleDraftCore, i rekkefølge. */
const STEPS: { key: string; label: string }[] = [
  { key: 'youtube', label: 'Henter relevante videoer' },
  { key: 'context', label: 'Leser tidligere artikler' },
  { key: 'writing', label: 'Skriver innhold' },
  { key: 'image', label: 'Lager illustrasjon' },
];

interface Props {
  articles: Article[];
  busy?: boolean;
  onOpen: (a: Article) => void;
  onPublish: (a: Article) => void;
  /** Godkjenn for planlagt dato (krever occasion_ymd på artikkelen). */
  onSchedule: (a: Article) => void;
}

/**
 * Hvilke kolonner en artikkel FAKTISK kan dras til.
 *
 * Ikke alle overganger er dine: `generating` styres av AI-en, og publisert kan
 * ikke angres. Det du styrer er godkjenningsbeslutningen — og den er naturlig å
 * dra. Ugyldige mål markeres ikke som droppsone, så gesten kan ikke lyve.
 */
const LEGAL_DROPS: Record<string, string[]> = {
  pending_review: ['approved', 'published'],
  approved: ['published'],
};

function firstImage(a: Article): string | null {
  if (!Array.isArray(a.images) || a.images.length === 0) return null;
  return [...a.images].sort((x, y) => (x.order || 0) - (y.order || 0))[0]?.url || null;
}

function shortDate(v: unknown): string {
  const d = v && typeof (v as { toDate?: () => Date }).toDate === 'function'
    ? (v as { toDate: () => Date }).toDate()
    : v instanceof Date ? v : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' });
}

const Column: React.FC<{
  title: string; count: number; children: React.ReactNode; hint?: string;
  droppable?: boolean; over?: boolean;
  onDragOver?: (e: React.DragEvent) => void; onDrop?: () => void; onDragLeave?: () => void;
}> = ({ title, count, children, hint, droppable, over, onDragOver, onDrop, onDragLeave }) => (
  <div
    className={`min-w-[15rem] lg:min-w-0 shrink-0 lg:shrink flex flex-col rounded-card transition snap-start lg:px-2 ${
      over && droppable ? 'bg-emerald-500/10 ring-1 ring-emerald-500/40'
        : droppable ? 'bg-emerald-500/[0.03]' : ''
    }`}
    onDragOver={onDragOver}
    onDrop={(e) => { e.preventDefault(); onDrop?.(); }}
    onDragLeave={onDragLeave}
  >
    <div className="flex items-center gap-2 px-1 pb-3">
      <h4 className="text-tiny font-black uppercase tracking-widest text-[var(--text-muted)]">{title}</h4>
      <span className="text-tiny font-black tabular-nums px-1.5 py-0.5 rounded-pill bg-[var(--bg-surface-alt)] text-[var(--text-secondary)]">
        {count}
      </span>
    </div>
    <div className="space-y-2 min-h-[4rem]">
      {count === 0 && hint ? (
        <p className="text-tiny text-[var(--text-muted)] px-1 leading-relaxed">{hint}</p>
      ) : children}
    </div>
  </div>
);

const Card: React.FC<{
  onClick?: () => void; children: React.ReactNode;
  draggable?: boolean; onDragStart?: () => void; onDragEnd?: () => void;
}> = ({ onClick, children, draggable, onDragStart, onDragEnd }) => (
  <div
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    draggable={draggable}
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
    onClick={onClick}
    onKeyDown={(e) => {
      if (onClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); }
    }}
    className={`rounded-card bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] p-3 space-y-2 transition ${
      draggable ? 'cursor-grab active:cursor-grabbing' : ''
    } ${onClick ? 'cursor-pointer hover:border-emerald-500/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60' : ''}`}
  >
    {children}
  </div>
);

const ArticleFlowBoard: React.FC<Props> = ({ articles, busy, onOpen, onPublish, onSchedule }) => {
  const [dragged, setDragged] = React.useState<Article | null>(null);
  const [overCol, setOverCol] = React.useState<string | null>(null);

  const canDrop = (col: string) => !!dragged && (LEGAL_DROPS[dragged.status] || []).includes(col);

  const handleDrop = (col: string) => {
    const a = dragged;
    setDragged(null);
    setOverCol(null);
    if (!a || !(LEGAL_DROPS[a.status] || []).includes(col)) return;
    if (col === 'published') onPublish(a);
    else if (col === 'approved') onSchedule(a);
  };

  const generating = articles.filter((a) => a.status === 'generating');
  const pending = articles.filter((a) => a.status === 'pending_review');
  const approved = articles.filter((a) => a.status === 'approved');
  const published = articles
    .filter((a) => a.status === 'published')
    .sort((a, b) => (b.view_count || 0) - (a.view_count || 0));

  return (
    <div className="flex lg:grid gap-4 lg:grid-cols-5 overflow-x-auto lg:overflow-visible snap-x snap-mandatory lg:snap-none -mx-1 px-1 pb-2 lg:pb-0 lg:divide-x divide-[var(--border-primary)]">
      {/* Crawleren finnes ikke ennå. En hardkodet 0 ville lest som «crawleren fant
          ingenting» i stedet for «crawleren er ikke bygd» — derfor forklart. */}
      <Column title="Kandidater" count={0} hint="Crawleren er ikke bygd ennå. Her havner nyhetssaker med relevansscore når den er på plass.">
        {null}
      </Column>

      <Column title="Genererer" count={generating.length} hint="Ingenting genereres nå." >
        {generating.map((a) => {
          const stepIdx = STEPS.findIndex((s) => s.key === (a as { _generation_step?: string })._generation_step);
          return (
            <Card key={a.id}>
              <p className="text-xs font-bold text-[var(--text-primary)] line-clamp-2">{a.title || 'Uten tittel'}</p>
              <div className="h-1 rounded-pill bg-[var(--bg-surface)] overflow-hidden">
                <div
                  className="h-full bg-emerald-400 rounded-pill transition-all"
                  style={{ width: `${Math.max(((stepIdx + 1) / STEPS.length) * 100, 8)}%` }}
                />
              </div>
              <ul className="space-y-1">
                {STEPS.map((s, i) => (
                  <li key={s.key} className="flex items-center gap-1.5 text-tiny">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      i < stepIdx ? 'bg-emerald-400'
                        : i === stepIdx ? 'bg-emerald-400 animate-pulse'
                        : 'border border-[var(--text-muted)]'
                    }`} />
                    <span className={i <= stepIdx ? 'text-[var(--text-secondary)]' : 'text-[var(--text-muted)]'}>
                      {s.label}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          );
        })}
      </Column>

      <Column title="Til godkjenning" count={pending.length} hint="Ingenting venter på deg.">
        {pending.map((a) => {
          const img = firstImage(a);
          return (
            <Card key={a.id} draggable={!busy} onDragStart={() => setDragged(a)} onDragEnd={() => { setDragged(null); setOverCol(null); }}>
              {img && <img src={img} alt="" className="w-full aspect-video object-cover rounded-card" />}
              <p className="text-xs font-bold text-[var(--text-primary)] line-clamp-2">{a.title || 'Uten tittel'}</p>
              <p className="text-tiny text-[var(--text-muted)] tabular-nums">{formatWordCount(a.content || '')}</p>
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => onOpen(a)}>Rediger</Button>
                <Button size="sm" variant="primary" disabled={busy} onClick={() => onPublish(a)}>Godkjenn</Button>
              </div>
            </Card>
          );
        })}
      </Column>

      <Column droppable={canDrop('approved')} over={overCol === 'approved'} onDragOver={(e) => { if (canDrop('approved')) { e.preventDefault(); setOverCol('approved'); } }} onDragLeave={() => setOverCol(null)} onDrop={() => handleDrop('approved')} title="Planlagt" count={approved.length} hint="Ingenting er planlagt fram i tid.">
        {approved.map((a) => (
          <Card key={a.id} onClick={() => onOpen(a)} draggable={!busy} onDragStart={() => setDragged(a)} onDragEnd={() => { setDragged(null); setOverCol(null); }}>
            <p className="text-xs font-bold text-[var(--text-primary)] line-clamp-2">{a.title || 'Uten tittel'}</p>
            {a.scheduled_publish_ymd && (
              <span className="inline-flex items-center gap-1.5 text-tiny tabular-nums text-[var(--text-muted)] px-2 py-1 rounded-pill bg-[var(--bg-surface)]">
                <Calendar size={10} /> {a.scheduled_publish_ymd}
              </span>
            )}
          </Card>
        ))}
      </Column>

      <Column droppable={canDrop('published')} over={overCol === 'published'} onDragOver={(e) => { if (canDrop('published')) { e.preventDefault(); setOverCol('published'); } }} onDragLeave={() => setOverCol(null)} onDrop={() => handleDrop('published')} title="Publisert" count={published.length} hint="Ingenting publisert ennå.">
        {published.slice(0, 8).map((a) => (
          <Card key={a.id} onClick={() => onOpen(a)}>
            <p className="text-xs font-bold text-[var(--text-primary)] line-clamp-2">{a.title || 'Uten tittel'}</p>
            <p className="text-tiny text-[var(--text-muted)] tabular-nums">{shortDate(a.published_at)}</p>
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1 text-tiny tabular-nums text-[var(--text-muted)]">
                <Eye size={10} /> {(a.view_count ?? 0).toLocaleString('nb-NO')}
              </span>
              <ViewSparkline viewsByDay={a.views_by_day} className="text-emerald-400/70" />
            </div>
          </Card>
        ))}
        {published.length > 8 && (
          <p className="text-tiny text-[var(--text-muted)] px-1">+ {published.length - 8} til i «Alle»</p>
        )}
      </Column>
    </div>
  );
};

export default ArticleFlowBoard;
